import React from "react";
import { Link } from "react-router-dom";
import { PRACTICE, NAV, ASSETS } from "@/data/site";
import { LOCAL_AREAS } from "@/data/localAreas";
import { useT } from "@/i18n/LanguageContext";

export default function Footer() {
  const { t } = useT();
  return (
    <footer
      data-testid="footer"
      className="px-6 md:px-10 lg:px-16 py-14 bg-[var(--surface)] border-t border-[var(--border)]"
    >
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-4">
            <img
              src={ASSETS.logo}
              alt="Smiles of Loudoun Dentistry"
              className="h-16 w-16 rounded-2xl object-cover"
            />
            <div className="leading-tight">
              <div className="font-serif-display text-2xl text-[var(--primary)]">
                Smiles <span className="italic text-[var(--accent)]">of</span> Loudoun
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--text-muted)] mt-1">
                Dentistry
              </div>
            </div>
          </div>
          <p className="mt-5 text-[var(--text-muted)] max-w-sm">
            {t("Family-first dentistry in Leesburg, Virginia. Proudly serving Ashburn, Broadlands, Belmont, Brambleton and neighboring cities in Northern Virginia.")}
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="font-mono-eyebrow text-[var(--text-muted)]">
            {t("Explore")}
          </div>
          <ul className="mt-4 space-y-2">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link
                  data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  to={n.to}
                  className="text-[var(--primary)] hover:text-[var(--accent)]"
                >
                  {t(n.label)}
                </Link>
              </li>
            ))}
            <li>
              <Link
                data-testid="footer-nav-insights"
                to="/insights"
                className="text-[var(--primary)] hover:text-[var(--accent)]"
              >
                {t("Insights")}
              </Link>
            </li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <div className="font-mono-eyebrow text-[var(--text-muted)]">{t("Visit")}</div>
          <a
            href={PRACTICE.mapUrl}
            target="_blank"
            rel="noreferrer"
            className="block mt-4 text-[var(--primary)] hover:text-[var(--accent)]"
            data-testid="footer-address-link"
          >
            {PRACTICE.address}
          </a>
          <a
            href={PRACTICE.phoneHref}
            className="block mt-2 text-[var(--primary)] hover:text-[var(--accent)]"
            data-testid="footer-phone-link"
          >
            {PRACTICE.phone}
          </a>
          <div className="mt-5 flex flex-wrap gap-2">
            <a
              data-testid="footer-pay-online"
              href={PRACTICE.payOnlineUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)]"
            >
              {t("Pay Online")}
            </a>
            <a
              data-testid="footer-refer"
              href={PRACTICE.referralUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)]"
            >
              {t("Refer a Patient")}
            </a>
          </div>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-[var(--border)]">
        <div className="font-mono-eyebrow text-[var(--text-muted)] mb-4">
          {t("Areas we serve")}
        </div>
        <ul
          data-testid="footer-local-links"
          className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-[var(--primary)]"
        >
          {LOCAL_AREAS.map((a) => (
            <li key={a.slug}>
              <Link
                data-testid={`footer-local-${a.slug}`}
                to={`/dentist-in/${a.slug}`}
                className="hover:text-[var(--accent)] underline-offset-4 hover:underline"
              >
                {t("Dentist in")} {a.city}, {a.state}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-12 pt-6 border-t border-[var(--border)] flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">
        <div>
          © {new Date().getFullYear()} Smiles of Loudoun. {t("All rights reserved.")}
        </div>
        <div>{t("Crafted with care in Leesburg, VA")}</div>
      </div>
    </footer>
  );
}
